import React from 'react';
import { Helmet } from 'react-helmet';
import { BOOKEND_TYPES, BookendsMetaTag, Header } from '@vp/esi-bookends';
import {
  getRootClassNames,
  ScreenClassProvider,
  SiteContent,
  SWAN_BASE_URL_MAP,
  SWAN_STYLE_KEY_MAP,
  SwanFontNameEnum,
  SwanHead,
  SwanProvider,
  useBrowserClasses,
} from '@vp/swan';
import { useTrackingContext } from './hooks/TrackingContextHooks';
import { baseTrackingConfiguration } from './providers/TrackingContextProvider';
import { ESI_BOOKEND_TENANT } from './utils/Constants';
import { currentCulture, currentLanguage } from './utils/cultureUtil';
import { text } from './utils/localizationUtil';

const eagerlyLoadedStyles = [
  SWAN_STYLE_KEY_MAP.carousel,
  SWAN_STYLE_KEY_MAP.accordion,
  SWAN_STYLE_KEY_MAP.toggleSwitch,
  SWAN_STYLE_KEY_MAP.standardTile,
  SWAN_STYLE_KEY_MAP.progressiveImage,
  SWAN_STYLE_KEY_MAP.core,
  SWAN_STYLE_KEY_MAP.button,
  SWAN_STYLE_KEY_MAP.modalDialog,
  SWAN_STYLE_KEY_MAP.alertBox,
  SWAN_STYLE_KEY_MAP.grid,
  SWAN_STYLE_KEY_MAP.hidden,
  SWAN_STYLE_KEY_MAP.icon,
  SWAN_STYLE_KEY_MAP.visible,
  SWAN_STYLE_KEY_MAP.utility,
  SWAN_STYLE_KEY_MAP.icon,
  SWAN_STYLE_KEY_MAP.visible,
  SWAN_STYLE_KEY_MAP.utility,
  SWAN_STYLE_KEY_MAP.spinner,
];

const swanFontFamilyNames = [SwanFontNameEnum.Graphik, SwanFontNameEnum.GraphikMedium, SwanFontNameEnum.GraphikRegular];

export interface Props {
  children?: React.ReactNode;
}
export const SwanConfiguration: React.FC<Props> = ({ children }: Props) => {
  const browser = useBrowserClasses();
  const rootClassName = getRootClassNames();
  const { trackingConfiguration } = useTrackingContext();
  const swanBaseUrl = SWAN_BASE_URL_MAP.default.cdn;
  return (
    <SwanProvider swanBaseUrl={swanBaseUrl}>
      <Helmet htmlAttributes={{ class: browser }} bodyAttributes={{ class: rootClassName }}>
        <html lang={currentLanguage} />
        <BookendsMetaTag
          locale={currentCulture}
          tenant={ESI_BOOKEND_TENANT}
          headerType={BOOKEND_TYPES.SLIM}
          footerType={BOOKEND_TYPES.SLIM}
          hideVat={false}
        />
        <title>Vistaprint | {text('page-title')}</title>
      </Helmet>
      <SwanHead
        renderStyleContentAsChildren
        renderWith={Helmet}
        styleSheetKeys={eagerlyLoadedStyles}
        fontNames={swanFontFamilyNames}
      />
      <SiteContent>
        <Header
          locale={currentCulture}
          tenant={ESI_BOOKEND_TENANT}
          type={BOOKEND_TYPES.SLIM}
          trackingConfiguration={trackingConfiguration ?? baseTrackingConfiguration}
        />
        <ScreenClassProvider>{children}</ScreenClassProvider>
      </SiteContent>
    </SwanProvider>
  );
};
